import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { tenantApi, clinicService } from "../services/api";
import CareLineHero from "../components/careline/CareLineHero";
import CareLineSteps from "../components/careline/CareLineSteps";
import CareLinePolyclinics from "../components/careline/CareLinePolyclinics";
import CareLineDoctors from "../components/careline/CareLineDoctors";
import CareLineTestimonials from "../components/careline/CareLineTestimonials";
import CareLineCTA from "../components/careline/CareLineCTA";
import CareLineLayout from "../components/careline/CareLineLayout";
import QueueModal from "../components/careline/QueueModal";
import QueueAlertModal from "../components/careline/QueueAlertModal";
import { useParams } from "react-router-dom";
import { useClinicDocumentMeta } from "../hooks/useClinicDocumentMeta";

// ─── Color palette  ──────────────────────────────────────
const COLORS = {
    primary: "#0891b2",
    primaryDark: "#0e7490",
    primaryLight: "#22d3ee",
    primaryGlow: "rgba(8,145,178,0.18)",
};

// ─── Page-level loading overlay  ────────────────────────
function PageLoader() {
    return (
        <motion.div
            key="page-loader"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-50 flex items-center justify-center"
            style={{
                backgroundColor: "rgba(0,0,0,0.15)",
                backdropFilter: "blur(8px)",
            }}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 8 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.22, ease: [0.22, 0.68, 0, 1.2] }}
                className="bg-white rounded-2xl px-8 py-7 flex flex-col items-center gap-4"
                style={{
                    boxShadow:
                        "0 20px 60px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.04)",
                    minWidth: "220px",
                }}
            >
                <svg
                    className="-rotate-90"
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                >
                    <circle
                        cx="20"
                        cy="20"
                        r="16"
                        fill="none"
                        stroke="#e2edef"
                        strokeWidth="3"
                    />
                    <circle
                        cx="20"
                        cy="20"
                        r="16"
                        fill="none"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeDasharray="50 101"
                        stroke="url(#cl-lg)"
                        style={{
                            animation: "cl-spin-dash 0.9s linear infinite",
                        }}
                    />
                    <defs>
                        <linearGradient
                            id="cl-lg"
                            x1="0%"
                            y1="0%"
                            x2="100%"
                            y2="0%"
                        >
                            <stop offset="0%" stopColor="#22d3ee" />
                            <stop offset="100%" stopColor="#0891b2" />
                        </linearGradient>
                    </defs>
                </svg>
                <div className="flex flex-col items-center gap-0.5 text-center">
                    <p
                        className="text-[0.82rem] font-semibold text-[#0d1a1c]"
                        style={{
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                        }}
                    >
                        Memuat data...
                    </p>
                    <p className="text-[0.72rem] text-[#8fa3a8]">
                        Harap tunggu sebentar
                    </p>
                </div>
            </motion.div>
        </motion.div>
    );
}

// ─── Poli skeleton card ───────────────────────────────────────────────────────
function PoliSkeleton() {
    return (
        <div className="w-full bg-white rounded-2xl p-6 border border-gray-100 flex flex-col gap-3 animate-pulse shadow-sm">
            <div className="w-12 h-12 rounded-xl bg-gray-100" />
            <div className="h-4 w-2/3 bg-gray-100 rounded-full" />
            <div className="h-3 w-full bg-gray-100 rounded-full" />
            <div className="h-3 w-5/6 bg-gray-100 rounded-full" />
            <div className="mt-2 space-y-2">
                <div className="h-2.5 w-full bg-gray-100 rounded-full" />
                <div className="h-2.5 w-4/5 bg-gray-100 rounded-full" />
            </div>
            <div className="h-10 w-full bg-gray-100 rounded-xl mt-3" />
        </div>
    );
}

// ─── Framer variants ──────────────────────────────────────────────────────────
const fadeUp = {
    hidden: { opacity: 0, y: 24 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.52,
            ease: [0.22, 0.68, 0, 1.1],
            delay: i * 0.1,
        },
    }),
};

const stagger = {
    hidden: {},
    show: { transition: { staggerChildren: 0.1 } },
};

// ─── Public content ───────────────────────────────────────────────────────────
export function CareLineContent({ logo, carouselImages, slug }) {
    return (
        <>
            <CareLineHero carouselImages={carouselImages} />
            <CareLineSteps />
            <CareLinePolyclinics slug={slug} />
            <CareLineDoctors slug={slug} />
            <CareLineTestimonials />
            <CareLineCTA slug={slug} />
        </>
    );
}

// ─── Dashboard (authenticated) ────────────────────────────────────────────────
const CareLineDashboard = ({ logo, carouselImages, slug, primaryColor }) => {
    const accent = primaryColor || COLORS.primary;
    const accentDark = COLORS.primaryDark;
    const accentLight = COLORS.primaryLight;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [activeQueue, setActiveQueue] = useState(null);
    const [polyclinics, setPolyclinics] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSyncing, setIsSyncing] = useState(false);

    useEffect(() => {
        if (!slug) return;
        fetchPolyclinics();

        const savedQueue = localStorage.getItem("activeQueue");
        if (savedQueue) {
            const queueData = JSON.parse(savedQueue);
            if (queueData.id_antrian || queueData.id) {
                setIsSyncing(true);
                syncActiveQueue(queueData.id_antrian || queueData.id);
            } else {
                localStorage.removeItem("activeQueue");
                setActiveQueue(null);
            }
        }
    }, [slug]);

    const getPoliPrefix = (poliName) => {
        const name = (poliName || "").toLowerCase();
        if (name.includes("umum")) return "A";
        if (name.includes("gigi")) return "C";
        if (name.includes("anak")) return "B";
        return name.charAt(0).toUpperCase() || "Q";
    };

    const syncActiveQueue = async (id) => {
        if (!id || !slug) return;
        try {
            const response = await tenantApi(slug).antrian.getById(id);
            if (response.data && response.data.success) {
                const currentStatus = response.data.data.status_antrian;
                if (currentStatus === "selesai" || currentStatus === "batal") {
                    localStorage.removeItem("activeQueue");
                    setActiveQueue(null);
                } else {
                    const queue = response.data.data;
                    const prefix = getPoliPrefix(queue.poli?.nama_poli);
                    const indexPart = String(queue.nomor_antrian).padStart(
                        2,
                        "0",
                    );
                    const enrichedQueue = {
                        ...queue,
                        number: `${prefix}${indexPart}`,
                        poly: queue.poli?.nama_poli || "Poli Umum",
                        doctor: queue.dokter?.nama_dokter || "Dokter",
                        type:
                            queue.jenis_antrian === "bpjs"
                                ? "Antrian BPJS"
                                : "Antrian Umum",
                        date: new Date(queue.tanggal).toLocaleDateString(
                            "id-ID",
                            {
                                weekday: "long",
                                day: "numeric",
                                month: "long",
                                year: "numeric",
                            },
                        ),
                        time: queue.jam ? queue.jam.substring(0, 5) : "--:--",
                        location: queue.poli
                            ? `Ruang ${queue.poli.nama_poli}`
                            : "Klinik",
                    };
                    setActiveQueue(enrichedQueue);
                    localStorage.setItem(
                        "activeQueue",
                        JSON.stringify(enrichedQueue),
                    );
                }
            } else {
                localStorage.removeItem("activeQueue");
                setActiveQueue(null);
            }
        } catch (error) {
            localStorage.removeItem("activeQueue");
            setActiveQueue(null);
            console.error("Error syncing queue:", error);
        } finally {
            setIsSyncing(false);
        }
    };

    const fetchPolyclinics = async () => {
        if (!slug) return;
        try {
            const response = await tenantApi(slug).poli.getAll();
            if (response.data.success) setPolyclinics(response.data.data);
        } catch (error) {
            console.error("Error fetching polyclinics:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => {
        if (isSyncing) return;
        if (activeQueue && activeQueue.number) setIsAlertModalOpen(true);
        else setIsModalOpen(true);
    };

    const handleQueueSuccess = (data) => {
        setActiveQueue(data);
        localStorage.setItem("activeQueue", JSON.stringify(data));
    };

    const getPoliIcon = (name) => {
        const n = (name || "").toLowerCase();
        if (n.includes("gigi")) return "/Icons/tooth.svg";
        if (n.includes("anak") || n.includes("ibu")) return "/Icons/baby.svg";
        return "/Icons/stetoscope.svg";
    };

    return (
        <CareLineLayout
            activePage="layanan"
            logo={logo}
            clinicName={null}
            slug={slug}
            primaryColor={primaryColor}
        >
            {/* ── HERO ──────────────────────────────────────────────────────── */}
            <section className="relative overflow-hidden">
                <div
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background: `linear-gradient(160deg, rgba(8,145,178,0.05) 0%, transparent 55%)`,
                    }}
                />

                <div className="container mx-auto px-5 sm:px-8 md:px-16 py-12 md:py-16 flex flex-col md:flex-row items-center justify-between gap-10 relative z-10">
                    {/* Text */}
                    <motion.div
                        className="max-w-xl w-full"
                        initial="hidden"
                        animate="show"
                        variants={stagger}
                    >
                        {/* Badge */}
                        <motion.div variants={fadeUp} custom={0}>
                            <div
                                className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-5"
                                style={{
                                    background: "rgba(8,145,178,0.08)",
                                    border: "1px solid rgba(8,145,178,0.2)",
                                }}
                            >
                                <span
                                    className="w-1.5 h-1.5 rounded-full"
                                    style={{
                                        backgroundColor: accent,
                                        animation:
                                            "cl-pulse 2s ease-in-out infinite",
                                    }}
                                />
                                <span
                                    className="text-[0.68rem] font-semibold tracking-[0.1em] uppercase"
                                    style={{
                                        color: accent,
                                        fontFamily:
                                            "'Plus Jakarta Sans', sans-serif",
                                    }}
                                >
                                    Antrian Digital Klinik
                                </span>
                            </div>
                        </motion.div>

                        {/* Headline — bigger */}
                        <motion.h1
                            variants={fadeUp}
                            custom={1}
                            className="text-4xl sm:text-5xl md:text-[3.25rem] font-bold leading-[1.15] tracking-tight mb-5 text-gray-900"
                            style={{
                                fontFamily: "'Plus Jakarta Sans', sans-serif",
                            }}
                        >
                            Layanan Antrian{" "}
                            <span
                                style={{
                                    background: `linear-gradient(135deg, ${accentLight} 0%, ${accent} 100%)`,
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                }}
                            >
                                Klinik
                            </span>
                            <br />
                            yang Cepat, Mudah,
                            <br />
                            <span className="text-gray-800">dan Efisien.</span>
                        </motion.h1>

                        {/* Sub */}
                        <motion.p
                            variants={fadeUp}
                            custom={2}
                            className="text-gray-500 text-base md:text-[1.05rem] mb-7 max-w-md leading-relaxed"
                        >
                            Ambil nomor antrian klinik dengan mudah tanpa perlu
                            datang langsung ke klinik. Pilih poli yang inginkan
                            dan tunggu giliran Anda dengan nyaman.
                        </motion.p>

                        {/* CTA */}
                        <motion.div variants={fadeUp} custom={3}>
                            <button
                                type="button"
                                onClick={handleOpenModal}
                                className="inline-flex items-center gap-2.5 px-7 py-3.5 text-white font-bold rounded-full transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 text-[0.92rem]"
                                style={{
                                    background:
                                        "linear-gradient(to right, #06B6D4, #0891B2)",
                                    boxShadow: `0 8px 28px rgba(8,145,178,0.35)`,
                                    fontFamily:
                                        "'Plus Jakarta Sans', sans-serif",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background =
                                        "linear-gradient(to right, #0891B2, #0e7490)";
                                    e.currentTarget.style.boxShadow = `0 12px 32px rgba(8,145,178,0.45)`;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background =
                                        "linear-gradient(to right, #06B6D4, #0891B2)";
                                    e.currentTarget.style.boxShadow = `0 8px 28px rgba(8,145,178,0.35)`;
                                }}
                            >
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="white"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <rect
                                        x="3"
                                        y="4"
                                        width="18"
                                        height="18"
                                        rx="2"
                                    />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                </svg>
                                Ambil Antrian Sekarang
                            </button>
                        </motion.div>
                    </motion.div>

                    {/* Image */}
                    <motion.div
                        className="relative shrink-0"
                        initial={{ opacity: 0, x: 32 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                            duration: 0.6,
                            delay: 0.2,
                            ease: [0.22, 0.68, 0, 1.1],
                        }}
                    >
                        <div
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[110%] rounded-full -z-10"
                            style={{
                                background: `radial-gradient(circle, rgba(8,145,178,0.08) 0%, transparent 70%)`,
                            }}
                        />
                        <div
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[80%] border border-dashed rounded-full -z-10"
                            style={{
                                borderColor: "rgba(8,145,178,0.15)",
                                animation: "cl-spin-slow 18s linear infinite",
                            }}
                        />
                        <img
                            src="/careline/careline-hero2.png"
                            alt="CareLine Doctors"
                            className="max-w-[240px] sm:max-w-xs md:max-w-md w-full h-auto drop-shadow-2xl"
                        />
                    </motion.div>
                </div>
            </section>

            {/* ── ACTIVE QUEUE or STEPS ─────────────────────────────────────── */}
            <AnimatePresence mode="wait">
                {activeQueue && !isSyncing && activeQueue.number ? (
                    <motion.section
                        key="active-queue"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        transition={{
                            duration: 0.4,
                            ease: [0.22, 0.68, 0, 1.1],
                        }}
                        className="container mx-auto px-5 sm:px-8 md:px-16 mb-16"
                    >
                        <div className="bg-white rounded-[28px] p-6 md:p-10 shadow-[0_20px_60px_rgba(0,0,0,0.07)] border border-gray-100 relative overflow-hidden">
                            <div
                                className="absolute top-0 right-0 w-48 h-48 rounded-full pointer-events-none"
                                style={{
                                    background: `radial-gradient(circle at top right, ${COLORS.primaryGlow} 0%, transparent 70%)`,
                                }}
                            />
                            <div className="relative z-10 bg-white rounded-2xl p-6 md:p-8 border border-gray-100 shadow-sm flex flex-col md:flex-row items-center gap-6 md:gap-0">
                                <div
                                    className="absolute top-[-18px] left-6 text-white px-5 py-2.5 rounded-full flex items-center gap-2 text-[11px] font-bold shadow-lg z-20"
                                    style={{
                                        background:
                                            "linear-gradient(to right, #06B6D4, #0891B2)",
                                        boxShadow: `0 6px 18px rgba(8,145,178,0.35)`,
                                    }}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="14"
                                        height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M5 22h14" />
                                        <path d="M5 2h14" />
                                        <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22" />
                                        <path d="M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />
                                    </svg>
                                    Nomor Antrian Anda
                                </div>

                                <div className="flex-1 flex justify-center items-center py-4 md:pr-10">
                                    <span
                                        className="text-7xl sm:text-8xl md:text-9xl font-black tracking-tighter"
                                        style={{
                                            color: accent,
                                            textShadow: `0 4px 20px ${accent}30`,
                                            fontFamily:
                                                "'Plus Jakarta Sans', sans-serif",
                                        }}
                                    >
                                        {activeQueue.number}
                                    </span>
                                </div>

                                <div className="hidden md:block w-px h-36 bg-gray-100" />

                                <div className="flex-[2] grid grid-cols-1 sm:grid-cols-2 gap-6 w-full md:pl-10">
                                    <div>
                                        <h4
                                            className="text-2xl sm:text-3xl font-bold mb-1"
                                            style={{ color: accent }}
                                        >
                                            {activeQueue.poly}
                                        </h4>
                                        <p className="text-gray-900 font-bold text-lg">
                                            {activeQueue.doctor}
                                        </p>
                                        <p className="text-gray-400 text-sm mt-0.5">
                                            {activeQueue.type}
                                        </p>
                                    </div>
                                    <div className="flex flex-col justify-center gap-3">
                                        <div className="text-gray-500 text-sm">
                                            <p>{activeQueue.date}</p>
                                            <p className="font-semibold text-gray-700">
                                                {activeQueue.time}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1.5 text-gray-400 text-xs">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke={accent}
                                                strokeWidth="2.5"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            >
                                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                                <circle cx="12" cy="10" r="3" />
                                            </svg>
                                            {activeQueue.location}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.section>
                ) : (
                    <motion.div
                        key="steps"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <CareLineSteps />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── PILIH POLI ────────────────────────────────── */}
            <section id="layanan" className="py-14 mb-14 overflow-hidden">
                <div className="container mx-auto px-5 sm:px-8 md:px-16">
                    <motion.div
                        className="mb-10 text-center"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{
                            duration: 0.5,
                            ease: [0.22, 0.68, 0, 1.1],
                        }}
                    >
                        <h2
                            className="text-3xl sm:text-4xl font-bold mb-3 text-gray-900"
                            style={{
                                fontFamily: "'Plus Jakarta Sans', sans-serif",
                            }}
                        >
                            Pilih{" "}
                            <span
                                style={{
                                    background: `linear-gradient(135deg, ${accentLight} 0%, ${accent} 100%)`,
                                    WebkitBackgroundClip: "text",
                                    WebkitTextFillColor: "transparent",
                                }}
                            >
                                Poli
                            </span>{" "}
                            Tujuan
                        </h2>
                        <p className="text-gray-400 text-sm">
                            Klik pada poli yang ingin Anda kunjungi untuk
                            mengambil nomor antrian
                        </p>
                    </motion.div>

                    <motion.div
                        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
                        initial="hidden"
                        whileInView="show"
                        viewport={{ once: true, margin: "-60px" }}
                        variants={stagger}
                    >
                        {isLoading ? (
                            <>
                                <PoliSkeleton />
                                <PoliSkeleton />
                                <PoliSkeleton />
                            </>
                        ) : polyclinics.length > 0 ? (
                            polyclinics.map((poli, idx) => (
                                <motion.div
                                    key={poli.id_poli}
                                    variants={fadeUp}
                                    custom={idx}
                                    whileHover={{
                                        y: -4,
                                        boxShadow:
                                            "0 20px 48px rgba(8,145,178,0.13)",
                                    }}
                                    className="w-full bg-white rounded-2xl p-6 border border-gray-100 flex flex-col items-start text-left shadow-sm"
                                >
                                    {/* Icon */}
                                    <div
                                        className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 shrink-0"
                                        style={{
                                            background:
                                                "linear-gradient(to right, #06B6D4, #0891B2)",
                                            boxShadow: `0 6px 16px rgba(8,145,178,0.28)`,
                                        }}
                                    >
                                        <img
                                            src={getPoliIcon(poli.nama_poli)}
                                            alt={poli.nama_poli}
                                            className="w-6 h-6 brightness-0 invert"
                                        />
                                    </div>

                                    <h3 className="text-base font-bold text-gray-900 mb-2">
                                        {poli.nama_poli}
                                    </h3>

                                    <p className="text-gray-400 text-xs leading-relaxed mb-5 flex-1">
                                        {poli.deskripsi ||
                                            "Pelayanan medis profesional untuk kebutuhan Anda."}
                                    </p>

                                    <div className="w-full mb-5 space-y-2">
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-400 text-[11px]">
                                                Jam Operasional
                                            </span>
                                            <span className="font-semibold text-gray-700 text-[11px]">
                                                {poli.jam_operasional || "—"}
                                            </span>
                                        </div>
                                        <div className="h-px bg-gray-50" />
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-400 text-[11px]">
                                                Antrian Saat Ini
                                            </span>
                                            <span className="font-semibold text-gray-700 text-[11px]">
                                                —
                                            </span>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleOpenModal}
                                        className="w-full py-2.5 text-white text-sm font-semibold rounded-xl transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                                        style={{
                                            background:
                                                "linear-gradient(to right, #06B6D4, #0891B2)",
                                            boxShadow: `0 4px 12px rgba(8,145,178,0.25)`,
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background =
                                                "linear-gradient(to right, #0891B2, #0e7490)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background =
                                                "linear-gradient(to right, #06B6D4, #0891B2)";
                                        }}
                                    >
                                        Ambil Antrian
                                    </button>
                                </motion.div>
                            ))
                        ) : (
                            <div className="col-span-3 py-16 text-center text-gray-300 text-sm">
                                Belum ada poli tersedia.
                            </div>
                        )}
                    </motion.div>
                </div>
            </section>

            <QueueModal
                slug={slug}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSuccess={handleQueueSuccess}
            />
            <QueueAlertModal
                isOpen={isAlertModalOpen}
                onClose={() => setIsAlertModalOpen(false)}
            />

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;700;800&display=swap');
                @keyframes cl-spin-dash {
                    0%   { stroke-dashoffset: 0; }
                    100% { stroke-dashoffset: -101; }
                }
                @keyframes cl-spin-slow {
                    from { transform: translate(-50%,-50%) rotate(0deg); }
                    to   { transform: translate(-50%,-50%) rotate(360deg); }
                }
                @keyframes cl-pulse {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50%      { opacity: 0.4; transform: scale(0.7); }
                }
            `}</style>
        </CareLineLayout>
    );
};

// ─── CareLine root ────────────────────────────────────────────────────────────
function CareLine() {
    const { slug } = useParams();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [clinicData, setClinicData] = useState(null);
    const [pageLoading, setPageLoading] = useState(true);

    useClinicDocumentMeta({
        title: clinicData?.nama_klinik
            ? `${clinicData.nama_klinik} — CareLine`
            : "CareLine",
        faviconUrl: clinicData?.logo_url || null,
        enabled: !!clinicData && !!slug,
    });

    useEffect(() => {
        const token = localStorage.getItem("token");
        setIsAuthenticated(!!token);
        if (slug) fetchClinicData();
    }, [slug]);

    const fetchClinicData = async () => {
        try {
            const response = await clinicService.getBySlug(slug);
            if (response.data.success) setClinicData(response.data.data);
        } catch (error) {
            console.error("Error fetching clinic data:", error);
        } finally {
            setPageLoading(false);
        }
    };

    return (
        <div className="flex min-h-screen flex-col font-body text-base-600 bg-white">
            <AnimatePresence>{pageLoading && <PageLoader />}</AnimatePresence>

            {!pageLoading &&
                (isAuthenticated ? (
                    <CareLineDashboard
                        slug={slug}
                        logo={clinicData?.logo_url}
                        carouselImages={clinicData?.carousel_images}
                        primaryColor={clinicData?.primary_color}
                    />
                ) : (
                    <CareLineLayout
                        slug={slug}
                        activePage="layanan"
                        logo={clinicData?.logo_url}
                        clinicName={clinicData?.nama_klinik}
                        primaryColor={clinicData?.primary_color}
                    >
                        <CareLineContent
                            slug={slug}
                            logo={clinicData?.logo_url}
                            carouselImages={clinicData?.carousel_images}
                        />
                    </CareLineLayout>
                ))}
        </div>
    );
}

export default CareLine;
